import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { Loader2, TrendingUp, Users, Eye, Smartphone } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AdminShell } from "@/components/admin/AdminShell";
import { SearchConsolePanel } from "@/components/admin/SearchConsolePanel";
import { getGrowthAnalytics, type GrowthAnalytics } from "@/lib/growth-analytics.functions";
import { referralBadge } from "@/lib/referral-tracker";

export const Route = createFileRoute("/admin/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics & Growth — WeLivdIt Admin" },
      { name: "description", content: "Traffic, acquisition channels and conversion funnel for WeLivdIt." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Analytics & Growth — WeLivdIt Admin" },
      { property: "og:description", content: "Traffic, acquisition channels and conversion funnel." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AnalyticsPage,
});

const RANGES = [7, 30, 90] as const;

type TabKey = "traffic" | "search";
const TABS: { key: TabKey; label: string }[] = [
  { key: "traffic", label: "Traffic & Acquisition" },
  { key: "search", label: "Google Search (SEO)" },
];

const inr = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

function AnalyticsPage() {
  const load = useServerFn(getGrowthAnalytics);
  const [tab, setTab] = useState<TabKey>("traffic");
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<GrowthAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await load({ data: { days } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load analytics.");
    } finally {
      setLoading(false);
    }
  }, [load, days]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const t = data?.totals;

  return (
    <AdminShell
      title="Analytics & Growth"
      description="Traffic growth, acquisition channels, top content and the visitor-to-student funnel."
    >
      <div className="space-y-8">
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className={`rounded-xl px-4 py-2 text-sm font-medium border transition ${
                tab === tb.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-accent"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={`rounded-full px-4 py-1.5 text-xs font-medium border transition ${
                days === r
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-accent"
              }`}
            >
              Last {r} days
            </button>
          ))}
        </div>

        {error && tab === "traffic" && (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {tab === "search" ? (
          <SearchConsolePanel days={days} />
        ) : loading || !data || !t ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi
                icon={<Users className="h-4 w-4" />}
                label="Unique visitors"
                value={t.visitors.toLocaleString("en-IN")}
                detail={
                  t.visitorGrowthPct === null
                    ? `vs ${t.prevVisitors} previous period`
                    : `${t.visitorGrowthPct >= 0 ? "+" : ""}${t.visitorGrowthPct}% vs previous ${days} days`
                }
              />
              <Kpi
                icon={<Eye className="h-4 w-4" />}
                label="Pageviews"
                value={t.views.toLocaleString("en-IN")}
                detail={`${t.viewsPerVisitor} pages per visitor`}
              />
              <Kpi
                icon={<Smartphone className="h-4 w-4" />}
                label="Mobile share"
                value={`${t.mobilePct}%`}
                detail={`${100 - t.mobilePct}% desktop`}
              />
              <Kpi
                icon={<TrendingUp className="h-4 w-4" />}
                label="Revenue in period"
                value={inr(t.revenueInr)}
                detail={`${t.paidStudents} paid students`}
              />
            </div>

            <section className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-semibold mb-4">Visitor growth</h2>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.daily} margin={{ left: -20, right: 8, top: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d: string) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Area
                      type="monotone"
                      dataKey="visitors"
                      name="Visitors"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary) / 0.15)"
                    />
                    <Area
                      type="monotone"
                      dataKey="views"
                      name="Pageviews"
                      stroke="hsl(var(--muted-foreground))"
                      fill="transparent"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="rounded-2xl border border-border bg-card p-5">
              <h2 className="font-semibold mb-1">Where visitors come from</h2>
              <p className="text-xs text-foreground/60 mb-4">
                Tag your links with <code>?ref=whatsapp</code> or <code>?ref=linkedin</code> for precise attribution.
              </p>
              {data.channels.length === 0 ? (
                <p className="text-sm text-foreground/60">No channel data in this period yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-foreground/60">
                      <tr>
                        <th className="text-left py-2">Channel</th>
                        <th className="text-right py-2">Visitors</th>
                        <th className="text-right py-2">Free session signups</th>
                        <th className="text-right py-2">Paid students</th>
                        <th className="text-right py-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.channels.map((c) => {
                        const badge = referralBadge(c.source === "unknown" ? null : c.source);
                        return (
                          <tr key={c.source} className="border-t border-border">
                            <td className="py-2">
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${badge.className}`}
                              >
                                {c.source === "unknown" ? "Unknown" : badge.label}
                              </span>
                            </td>
                            <td className="py-2 text-right tabular-nums">{c.visitors}</td>
                            <td className="py-2 text-right tabular-nums">{c.registrations}</td>
                            <td className="py-2 text-right tabular-nums">{c.paidStudents}</td>
                            <td className="py-2 text-right tabular-nums">{inr(c.revenueInr)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-2xl border border-border bg-card p-5">
                <h2 className="font-semibold mb-4">Top pages</h2>
                {data.topPages.length === 0 ? (
                  <p className="text-sm text-foreground/60">No pageviews recorded yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.topPages.map((p) => (
                      <li key={p.page} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate font-mono text-xs">{p.page}</span>
                        <span className="whitespace-nowrap text-foreground/70">
                          {p.views} views · {p.visitors} visitors
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="rounded-2xl border border-border bg-card p-5">
                <h2 className="font-semibold mb-4">Visitor → student funnel</h2>
                <FunnelStep label="Site visitors" value={t.visitors} max={t.visitors} />
                <FunnelStep label="Free session signups" value={t.registrations} max={t.visitors} />
                <FunnelStep label="Paid students" value={t.paidStudents} max={t.visitors} />
                <p className="mt-4 text-xs text-foreground/60">
                  End-to-end conversion:{" "}
                  <strong>
                    {t.visitors ? ((t.paidStudents / t.visitors) * 100).toFixed(2) : "0.00"}%
                  </strong>{" "}
                  of visitors become paid students.
                </p>
              </section>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function Kpi({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/60">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
      <p className="mt-1 text-xs text-foreground/60">{detail}</p>
    </div>
  );
}

function FunnelStep({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="tabular-nums font-medium">{value.toLocaleString("en-IN")}</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
